import type { ReactNode } from 'react';

import { InfoIcon } from 'lucide-react';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { SELECTED_SURFACE } from '@/components/ui/constants';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

type KpiTileProps = {
  label: string;
  value: ReactNode;
  isLoading?: boolean;
  isSelected?: boolean;
  onClick?: () => void;
  comparison?: ReactNode;
  className?: string;
  tooltip?: string;
};

export const KpiTile = ({
  label,
  value,
  isLoading,
  isSelected,
  onClick,
  comparison,
  className,
  tooltip,
}: KpiTileProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    className={cn(
      // A button centres its content box, so in a stretched grid row a shorter tile
      // would float its label off the shared top line.
      'flex flex-col items-stretch justify-start',
      'rounded-lg border p-4 text-left transition-colors',
      'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none',
      isSelected ? SELECTED_SURFACE : 'border-border bg-background hover:bg-muted/50',
      !onClick && 'cursor-default hover:bg-background',
      className
    )}
  >
    <div className="flex items-center gap-1">
      <p className="text-xs leading-4 font-medium text-muted-foreground">{label}</p>
      {tooltip && (
        <InfoTooltip content={tooltip} size="large">
          <InfoIcon className="size-3 shrink-0 text-muted-foreground" />
        </InfoTooltip>
      )}
    </div>
    <div className="mt-4 text-2xl leading-none font-semibold">
      {isLoading ? <Skeleton className="h-9 w-20" /> : value}
    </div>
    {comparison}
  </button>
);
